import type { CogoElement } from "../core/types"
import { liftV4Props } from "./element-renderer"

type ResolveSymbol = (value: string) => unknown

export function readResolvedArray(
  element: CogoElement,
  property: string,
  resolveSymbol: ResolveSymbol,
): unknown[] {
  const raw = (element as unknown as Record<string, unknown>)[property]
  const resolved = typeof raw === "string" ? resolveSymbol(raw) : raw
  return Array.isArray(resolved) ? resolved : []
}

/**
 * Single interpreter for "is this raw array entry readable as a record" —
 * JITRULE Task 2 (2026-08-07). The rule this enforces is declared in the v4
 * schema (`cogo-schema/v4/ui.cogo.schema.json` `$defs.Element.props`,
 * mirrored in `cogo-types-v2/src/schema/element.ts`'s `UiElement.props`):
 * any v4-schema element's renderable fields may be authored at the top
 * level OR nested under `props`, and producers routinely choose the latter
 * (see `apps/cogo-starter/public/apps/fwd2-expense-approval` for real
 * examples). `recordValue` used to hand callers the raw, unlifted item —
 * every one of the 12 renderers that import it from here believed they were
 * already "using the shared helper" and were not (see the census's "false
 * shared helper" finding). Delegating to `liftV4Props` — the SAME function
 * `element-renderer.tsx`'s own `normalizeElement` uses for the top-level
 * element — means there is exactly one place that knows the discovery-order
 * rule, not a second, independently-maintained copy of it here.
 */
export function recordValue(value: unknown): Record<string, unknown> {
  const record = value !== null && typeof value === "object" && !Array.isArray(value)
    ? value as Record<string, unknown>
    : { label: String(value ?? "") }
  return liftV4Props(record)
}

export function resolvedText(
  value: unknown,
  resolveSymbol: ResolveSymbol,
  fallback = "",
): string {
  if (value === undefined || value === null) return fallback
  const resolved = typeof value === "string" ? resolveSymbol(value) : value
  return resolved === undefined || resolved === null ? fallback : String(resolved)
}

export function positiveInteger(value: unknown, fallback: number, max = 100): number {
  const numeric = Number(value)
  if (!Number.isFinite(numeric) || numeric <= 0) return fallback
  return Math.min(max, Math.max(1, Math.floor(numeric)))
}

export function finiteNumber(value: unknown, fallback: number): number {
  const numeric = Number(value)
  return Number.isFinite(numeric) ? numeric : fallback
}

export function safeResourceUrl(value: unknown): string | undefined {
  if (typeof value !== "string") return undefined
  const url = value.trim()
  if (!url) return undefined
  if (/^(?:https?:|data:image\/|blob:)/i.test(url)) return url
  if (/^(?:\/|\.\/|\.\.\/)/.test(url) && !url.startsWith("//")) return url
  return undefined
}

export function safeNavigationUrl(value: unknown): string | undefined {
  if (typeof value !== "string") return undefined
  const url = value.trim()
  if (!url) return undefined
  if (/^(?:https?:|mailto:|tel:|#)/i.test(url)) return url
  if (/^(?:\/|\.\/|\.\.\/)/.test(url) && !url.startsWith("//")) return url
  return undefined
}

export function shorthandId(parentId: string, property: string, index: number): string {
  return `${parentId}__${property}_${index}`
}
